// Root shell. Picks Public vs Protected based on the current pathname.
//
// PublicShell: bare <Outlet />. Used for /welcome, /login, /governance.
// Each public page renders its own minimal header / structure — no protected
// chrome ever appears on these routes.
//
// ProtectedShell: the authenticated app chrome (nav, user menu, toasts).
// Guarded by the /api/v1/me query — until that query resolves, the protected
// chrome is NEVER rendered. While /me is loading, a minimal wordmark-only
// loading state shows. On 401 the user is redirected to /welcome (the
// friendlier of the two public entry points).
//
// This split fixes the "unauthenticated flash" bug where the protected
// chrome was rendering optimistically before the /me query had a chance to
// 401. The fix is structural: the auth check is now the gate, not an
// afterthought.

import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from './lib/api';
import { useToasts } from './hooks/useToasts';
import { useGlobalShortcuts } from './hooks/useGlobalShortcuts';
import { DeadlineRadar } from './components/DeadlineRadar';

interface MeResponse {
  id: string;
  email: string;
  display_name: string;
  role: 'admin' | 'grants_researcher' | 'viewer';
}

export interface AppContext {
  showShortcuts: boolean;
  setShowShortcuts: (v: boolean) => void;
  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
}

const AppContextRef = React.createContext<AppContext>({
  showShortcuts: false, setShowShortcuts: () => {},
  paletteOpen: false, setPaletteOpen: () => {},
});

export function useAppContext(): AppContext {
  return React.useContext(AppContextRef);
}

const PUBLIC_PATHS = ['/welcome', '/login', '/governance'];
function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

export default function App() {
  const { pathname } = useLocation();
  return isPublicPath(pathname) ? <PublicShell /> : <ProtectedShell />;
}

function PublicShell() {
  // Each public route owns its own structure. The shell adds nothing here so
  // public pages cannot accidentally inherit protected chrome.
  return <Outlet />;
}

function LoadingShell() {
  return (
    <div className="min-h-full flex items-center justify-center" role="status" aria-live="polite">
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tracking-tight text-accent">Granted</span>
        <span className="text-sm text-neutral-500">by Commondoor</span>
      </div>
      <span className="sr-only">Loading…</span>
    </div>
  );
}

function ProtectedShell() {
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const toasts = useToasts();
  const navigate = useNavigate();

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/me'),
    retry: false,
  });

  useGlobalShortcuts({
    onPalette: () => setPaletteOpen(true),
    onHelp: () => setShowShortcuts(true),
    onCloseOverlays: () => { setShowShortcuts(false); setPaletteOpen(false); },
  });

  // The gate. While /me is pending we render a neutral loading shell; on 401
  // we redirect to /welcome before the protected chrome ever paints.
  useEffect(() => {
    if (me.error instanceof ApiError && me.error.status === 401) {
      navigate({ to: '/welcome', replace: true });
    }
  }, [me.error, navigate]);

  if (me.isLoading) return <LoadingShell />;
  if (me.error || !me.data) {
    // Either the 401 path (navigating away) or an unexpected error. Show the
    // loading shell so we never flash protected chrome with no user.
    return <LoadingShell />;
  }

  const user = me.data.data;

  return (
    <AppContextRef.Provider value={{ showShortcuts, setShowShortcuts, paletteOpen, setPaletteOpen }}>
      <div className="min-h-full flex flex-col">
        <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-0 focus:left-0 focus:p-2 focus:bg-white focus:z-50">
          Skip to main content
        </a>
        <header className="h-14 border-b border-neutral-200 flex items-center px-4 gap-6 shrink-0">
          <Link to="/" className="flex items-baseline gap-1.5 tracking-tight" aria-label="Granted, by Commondoor">
            <span className="font-semibold text-accent">Granted</span>
            <span className="text-xs text-neutral-500">by Commondoor</span>
          </Link>
          <nav className="text-sm text-neutral-600 flex gap-4">
            <Link to="/" className="hover:text-neutral-900" activeProps={{ className: 'text-neutral-900 underline underline-offset-4' }}>Triage</Link>
            <Link to="/projects" className="hover:text-neutral-900" activeProps={{ className: 'text-neutral-900 underline underline-offset-4' }}>Projects</Link>
            <Link to="/partners" className="hover:text-neutral-900" activeProps={{ className: 'text-neutral-900 underline underline-offset-4' }}>Partners</Link>
            <Link to="/applications" className="hover:text-neutral-900" activeProps={{ className: 'text-neutral-900 underline underline-offset-4' }}>Applications</Link>
            <Link to="/finance" className="hover:text-neutral-900" activeProps={{ className: 'text-neutral-900 underline underline-offset-4' }}>Finance</Link>
            <Link to="/snapshot" className="hover:text-neutral-900" activeProps={{ className: 'text-neutral-900 underline underline-offset-4' }}>Snapshot</Link>
            <Link to="/admin/verify" className="hover:text-neutral-900" activeProps={{ className: 'text-neutral-900 underline underline-offset-4' }}>Verification</Link>
            <Link to="/admin/sources" className="hover:text-neutral-900" activeProps={{ className: 'text-neutral-900 underline underline-offset-4' }}>Sources</Link>
            <Link to="/admin/audit" className="hover:text-neutral-900" activeProps={{ className: 'text-neutral-900 underline underline-offset-4' }}>Audit</Link>
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <DeadlineRadar />
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="text-xs text-neutral-600 border border-neutral-300 rounded px-2 py-1 hover:bg-neutral-50"
              aria-label="Open command palette"
            >
              <kbd className="font-mono">⌘K</kbd>
            </button>
            <div className="text-sm text-neutral-600">
              <span className="text-neutral-900">{user.display_name}</span>
              <span className="ml-2 text-xs uppercase tracking-wide text-neutral-500">{user.role}</span>
            </div>
          </div>
        </header>
        <main id="main" className="flex-1 min-h-0">
          <Outlet />
        </main>
        {/* aria-live region for toasts */}
        <div aria-live="polite" aria-atomic="false" className="fixed bottom-4 right-4 space-y-2 z-50">
          {toasts.map((t) => (
            <div key={t.id} className={
              'rounded-md shadow-md px-3 py-2 text-sm border flex items-center gap-3 ' +
              (t.kind === 'error' ? 'bg-rose-50 border-rose-200 text-rose-800' :
               t.kind === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                                      'bg-white border-neutral-200 text-neutral-800')
            }>
              <span className="flex-1">{t.message}</span>
              {t.action && (
                <button
                  type="button"
                  onClick={t.action.onClick}
                  className={
                    'text-xs font-medium underline-offset-2 hover:underline rounded px-1 ' +
                    (t.kind === 'error' ? 'text-rose-900' :
                     t.kind === 'success' ? 'text-emerald-900' :
                     'text-neutral-900')
                  }
                >
                  {t.action.label}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </AppContextRef.Provider>
  );
}
